import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, CalendarDays, Baby, Shirt, Clock, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { Reveal, LineReveal, Stagger, StaggerItem } from "@/components/Reveal";
import { LOCATIONS } from "@/data/locations";
import { useSettings, DIRECTIONS_URL, AREA_DESCRIPTOR, formatAddress } from "@/hooks/SettingsContext";
import { trackPlanVisitClick, trackDirectionsClick, trackPlanVisitSubmit } from "@/lib/analytics";
import api, { formatApiError } from "@/lib/api";
import { Field, TextInput, SubmitButton, inputCls, HEAR_OPTIONS } from "@/components/forms";
import InviteShare from "@/components/InviteShare";
import LaunchTextCapture from "@/components/LaunchTextCapture";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const OAKS_IMG = "/oak-trees.jpg";

// Legacy quick-visit slugs that have a corresponding full local guide (/church-{city}-fl)
const LOCAL_GUIDE = {
  middleburg: "church-middleburg-fl",
  "lake-asbury": "church-lake-asbury-fl",
  oakleaf: "church-oakleaf-fl",
  "orange-park": "church-orange-park-fl",
  "fleming-island": "church-fleming-island-fl",
  "green-cove-springs": "church-green-cove-springs-fl",
};

const GLANCE = [
  { icon: CalendarDays, label: "First Service", value: "Service #1 - January 2027" },
  { icon: MapPin, label: "Address", value: "3180 CR 220 STE 6-7, Middleburg, FL 32068" },
  { icon: Baby, label: "Kids", value: "VC Kids nursery & children's church" },
  { icon: Shirt, label: "Dress", value: "Come as you are - no dress code" },
];

const VISIT_POINTS = [
  "We'll send you everything you need before launch day",
  "We'll be watching for you at the door",
  "VC Kids ready for your little ones",
  "60-minute service - come as you are",
];

function LocationVisitForm({ loc }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", hear_about: "", connect_ok: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/plan-visit", { ...form, location: loc.name });
      trackPlanVisitSubmit();
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white py-16 sm:py-24" data-testid="location-visit-section">
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 sm:gap-12 lg:grid-cols-2">
        <Reveal>
          <p className="font-display text-sm font-700 uppercase tracking-[0.3em] text-victory">Service #1 - January 2027</p>
          <h2 className="mt-3 text-balance font-display text-3xl font-900 tracking-tighter text-navy sm:text-5xl">
            PLAN YOUR VISIT FROM {loc.name.toUpperCase()}.
          </h2>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600">
            Thirty seconds now, and we'll roll out the welcome mat for you in January.
          </p>
          <ul className="mt-6 space-y-3">
            {VISIT_POINTS.map((p) => (
              <li key={p} className="flex items-center gap-3 text-sm font-medium text-navy">
                <CheckCircle2 size={18} className="shrink-0 text-victory" aria-hidden="true" /> {p}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.12}>
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
              className="rounded-3xl bg-navy p-10 text-center text-white shadow-lift" data-testid="location-visit-success">
              <h3 className="font-display text-3xl font-800 tracking-tight">WE CAN'T WAIT TO MEET YOU!</h3>
              <p className="mt-4 text-white/75">
                We'll send you the details for Service #1 and a reminder as launch day gets closer.
              </p>
              <InviteShare />
            </motion.div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl bg-cream p-6 shadow-lift sm:p-9" data-testid="location-visit-form">
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="First Name" id="lv-first" required>
                  <TextInput id="lv-first" required value={form.first_name} onChange={set("first_name")} autoComplete="given-name" />
                </Field>
                <Field label="Last Name" id="lv-last" required>
                  <TextInput id="lv-last" required value={form.last_name} onChange={set("last_name")} autoComplete="family-name" />
                </Field>
                <Field label="Email" id="lv-email" required>
                  <TextInput id="lv-email" type="email" required value={form.email} onChange={set("email")} autoComplete="email" />
                </Field>
                <Field label="Phone" id="lv-phone" hint="Optional">
                  <TextInput id="lv-phone" type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Where did you hear about us?" id="lv-hear" hint="Optional">
                    <select id="lv-hear" data-testid="lv-hear" value={form.hear_about} onChange={set("hear_about")} className={inputCls}>
                      <option value="">Select one...</option>
                      {HEAR_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                </div>
                <label htmlFor="lv-connect" className="flex cursor-pointer items-start gap-3 text-sm text-slate-600 sm:col-span-2">
                  <input id="lv-connect" type="checkbox" data-testid="lv-connect" checked={form.connect_ok}
                         onChange={(e) => setForm((f) => ({ ...f, connect_ok: e.target.checked }))}
                         className="mt-1 h-4 w-4 rounded border-slate-300 accent-[#C8102E]" />
                  Yes, it's okay to connect with us ahead of time.
                </label>
              </div>
              <div className="mt-6">
                <SubmitButton testId="location-visit-submit" loading={loading}>PLAN MY VISIT</SubmitButton>
                <p className="mt-4 text-xs leading-relaxed text-slate-500" data-testid="location-visit-note">
                  We'll reach out with everything you need - and on launch day, we'll be watching for you at the door.
                </p>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}

export default function LocationPage({ slug }) {
  const loc = LOCATIONS[slug];
  const { settings } = useSettings();
  if (!loc) return null;
  const s = settings || {};

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Church",
        name: "Victory Chapel Clay",
        alternateName: ["Victory Chapel Christian Church", "VC Clay"],
        description: `Plan your first visit to Victory Chapel Clay from ${loc.name} - a new Christian church located at 3180 CR 220 STE 6-7, Middleburg, FL, launching January 2027.`,
        url: `https://vcclaychurch.com/${slug}`,
        address: {
          "@type": "PostalAddress",
          streetAddress: "3180 CR 220 STE 6-7",
          addressLocality: "Middleburg",
          addressRegion: "FL",
          postalCode: "32068",
          addressCountry: "US",
        },
        areaServed: [loc.name, "Clay County"],
        telephone: "+1-904-383-2559",
        email: "Victorychapelclay@gmail.com",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://vcclaychurch.com/" },
          { "@type": "ListItem", position: 2, name: `Plan Your Visit from ${loc.name}` },
        ],
      },
      {
        "@type": "FAQPage",
        mainEntity: loc.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
    ],
  };

  return (
    <>
      <SEO
        title={`Plan Your Visit from ${loc.name}`}
        description={`Planning your first visit to Victory Chapel Clay from ${loc.name}, FL? Directions, kids, and what to expect - a new church in Middleburg launching Jan 2027.`}
        path={`/${slug}`}
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden bg-navy py-16 text-white sm:py-24">
        <img src={OAKS_IMG} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20" loading="eager" />
        <div className="absolute inset-0 bg-navy/70" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-white/60" data-testid="breadcrumb">
            <Link to="/" className="transition-colors hover:text-white">Home</Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page" className="text-white/85">Plan Your Visit from {loc.name}</span>
          </nav>
          <p className="font-display text-sm font-700 uppercase tracking-[0.35em] text-rose-300">Plan Your Visit - {loc.name}, FL</p>
          <LineReveal className="mt-4 font-display text-4xl font-black leading-[1.02] tracking-tighter sm:text-6xl lg:text-7xl" lines={["PLANNING YOUR FIRST VISIT", `FROM ${loc.name.toUpperCase()}?`]} />
          <Reveal delay={0.4}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
              Here's everything you need before launch - where Victory Chapel Clay is, how to get here
              from {loc.name}, what to expect when you arrive, and how we'll be ready to welcome you
              when doors open in January 2027.
            </p>
          </Reveal>
          <Reveal delay={0.5}>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <Link to="/im-new" onClick={trackPlanVisitClick} data-testid="location-plan-visit-cta"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-victory px-8 py-4 font-display text-base font-700 text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-victory-dark">
                PLAN YOUR VISIT <ArrowRight size={18} aria-hidden="true" />
              </Link>
              <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" onClick={trackDirectionsClick} data-testid="location-directions-cta"
                 className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-8 py-4 font-display text-base font-700 text-white transition-colors duration-300 hover:bg-white/10">
                GET DIRECTIONS <MapPin size={18} aria-hidden="true" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-14 sm:py-20" data-testid="location-glance-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-3xl bg-navy p-7 text-white shadow-lift sm:p-10">
              <p className="font-display text-xs font-700 uppercase tracking-[0.3em] text-rose-300">Service #1 - at a glance</p>
              <h2 className="mt-3 font-display text-2xl font-800 tracking-tight sm:text-3xl text-balance">
                BE THERE FROM THE BEGINNING.
              </h2>
              <Stagger className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {GLANCE.map((g) => (
                  <StaggerItem key={g.label}>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-5" data-testid={`glance-${g.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      <g.icon size={20} className="text-victory" aria-hidden="true" />
                      <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-white/50">{g.label}</p>
                      <p className="mt-1 text-sm font-semibold text-white">{g.value}</p>
                    </div>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-24" data-testid="location-body-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 sm:gap-12 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-3xl font-800 tracking-tight text-navy sm:text-4xl text-balance">
              WHAT TO EXPECT WHEN YOU VISIT FROM {loc.name.toUpperCase()}.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">{loc.intro}</p>
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">{loc.body}</p>
            <p className="mt-6 rounded-2xl border border-navy/10 bg-cream px-5 py-4 text-sm leading-relaxed text-slate-600">
              Victory Chapel Clay has one home - 3180 CR 220 STE 6-7 in Middleburg - serving families
              from {loc.name} and communities across Clay County.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="rounded-3xl bg-cream p-7 sm:p-10">
              <h3 className="font-display text-xl font-800 tracking-tight text-navy">GETTING HERE FROM {loc.name.toUpperCase()}</h3>
              <p className="mt-4 leading-relaxed text-slate-600">{loc.drive}</p>
              <ul className="mt-6 space-y-3 text-sm text-slate-600">
                <li className="flex gap-3"><MapPin size={16} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" /> {formatAddress(s) || AREA_DESCRIPTOR}</li>
                <li className="flex gap-3"><Clock size={16} className="mt-0.5 shrink-0 text-victory" aria-hidden="true" />
                  {s.launch_mode !== false ? "Service #1 - January 2027" : ([s.sunday_time, s.wednesday_time].filter(Boolean).join(" - ") || "Weekly service times coming soon")}
                </li>
                {s.parking_info_text && (
                  <li className="flex gap-3"><span className="mt-0.5 h-1.5 w-1.5 shrink-0 translate-y-1.5 rounded-full bg-victory" aria-hidden="true" /> {s.parking_info_text}</li>
                )}
              </ul>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/im-new" data-testid="location-expect-link"
                      className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-5 py-2.5 font-display text-xs font-700 text-navy transition-colors hover:bg-navy hover:text-white">
                  WHAT TO EXPECT <ArrowRight size={13} aria-hidden="true" />
                </Link>
                <Link to="/kids" data-testid="location-kids-link"
                      className="inline-flex items-center gap-1.5 rounded-full border border-navy/20 px-5 py-2.5 font-display text-xs font-700 text-navy transition-colors hover:bg-navy hover:text-white">
                  EXPLORE VC KIDS <ArrowRight size={13} aria-hidden="true" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <LocationVisitForm loc={loc} />

      <section className="bg-cream py-16 sm:py-20" data-testid="location-faq-section">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display text-3xl font-800 tracking-tight text-navy sm:text-4xl">
              QUESTIONS ABOUT VISITING FROM {loc.name.toUpperCase()}
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <Accordion type="single" collapsible className="mt-8" data-testid="location-faq-accordion">
              {loc.faqs.map((f, i) => (
                <AccordionItem key={i} value={`lf-${i}`} className="border-navy/10">
                  <AccordionTrigger data-testid={`location-faq-trigger-${i}`} className="text-left font-display text-lg font-700 text-navy hover:text-victory">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-base leading-relaxed text-slate-600">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-14 sm:py-16" data-testid="location-nearby-section">
        <div className="mx-auto max-w-7xl px-5 text-center sm:px-8">
          <Reveal>
            <p className="font-display text-xs font-700 uppercase tracking-[0.3em] text-victory">Also near you</p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              {loc.nearby.map((n) => (
                <Link key={n} to={`/${n}`} data-testid={`nearby-${n}`}
                      className="rounded-full border border-navy/15 bg-cream px-6 py-3 font-display text-sm font-700 text-navy transition-all duration-300 hover:-translate-y-0.5 hover:border-navy hover:shadow-soft">
                  Visiting from {LOCATIONS[n].name}
                </Link>
              ))}
            </div>
            <p className="mx-auto mt-8 max-w-xl text-sm leading-relaxed text-slate-500">
              Victory Chapel is a new church in Clay County, Florida, serving Lake Asbury, Middleburg,
              Oakleaf, Fleming Island, Orange Park and surrounding communities.
            </p>
            {LOCAL_GUIDE[slug] && (
              <p className="mt-4 text-sm text-slate-500">
                Still deciding? <Link to={`/${LOCAL_GUIDE[slug]}`} data-testid="location-guide-link" className="font-semibold text-victory transition-colors hover:text-victory-dark">Looking for a church {slug === "middleburg" ? "in" : "near"} {loc.name}? Start with our local guide</Link>
              </p>
            )}
          </Reveal>
        </div>
      </section>

      <LaunchTextCapture page={`/${slug}`} />
    </>
  );
}
